import {UserAccountApi} from '@frontend/api/UserAccountApi'
import {AppConfig} from '@frontend/AppConfig'
import {useUserData} from '@frontend/hooks'
import {
    useAccountFlagsStore,
    useAuthStore,
} from '@frontend/stores'
import {DataPolling} from 'swayok-react-mdb-ui-kit/components/DataPolling/DataPolling'
import {UnauthorisedErrorHttpCode} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {CacheService} from '../../services/CacheService'
import type {UserAccountFlags} from '../../types/GlobalStores'

// Поллинг данных пользователя.
export function GlobalDataPolling() {

    const reloadUserData = useAuthStore(
        state => state.reloadUserData
    )
    const onNotAuthorizedApiError = useAuthStore(
        state => state.onNotAuthorizedApiError
    )
    const isLoggedIn = !!useUserData()
    const setFlags = useAccountFlagsStore(
        state => state.setFlags
    )

    // Получить актуальные данные UI флагов пользователя (из кеша или загрузить из API).
    const handleUserFlagsPolling = useEventCallback(async (
        requestConfig: RequestInit
    ): Promise<void> => {
        const cachedFlags: UserAccountFlags | null = CacheService.get<UserAccountFlags>('user-flags-polling')
        if (cachedFlags) {
            setFlags(cachedFlags)
            return Promise.resolve()
        }
        try {
            const flags: UserAccountFlags = await UserAccountApi.getFlags(undefined, requestConfig)
            setFlags(flags)
            CacheService.set(
                'user-flags-polling',
                flags,
                Math.ceil((AppConfig.flagsPollingInterval * 0.8) / 60000)
            )
        } catch {
        }
    })

    // Получить актуальные данные пользователя (из кеша или загрузить из API).
    const handleUserInfoPolling = useEventCallback(async (
        requestConfig: RequestInit
    ): Promise<unknown> => reloadUserData(true, true, requestConfig)
        .catch((error: ApiError) => {
            if (error.status === UnauthorisedErrorHttpCode) {
                // Ошибка авторизации - выходим из ЛК.
                onNotAuthorizedApiError()
            }
        }))

    return (
        <>
            <DataPolling
                // Периодическое обновление данных пользователя.
                key="user-data-polling"
                condition={isLoggedIn}
                name={AppConfig.pollingName.userInfo}
                interval={AppConfig.userInfoPollingInterval}
                handler={handleUserInfoPolling}
            />
            <DataPolling
                // Периодическое обновление состояния различных флагов аккаунта.
                key="user-flags-polling"
                condition={isLoggedIn}
                name={AppConfig.pollingName.flags}
                interval={AppConfig.flagsPollingInterval}
                immediate
                handler={handleUserFlagsPolling}
            />
        </>
    )
}
